import React, { useState } from 'react';
import { Button, Typography, Box, Card, CardContent, Slider, TextField, InputAdornment, LinearProgress } from '@mui/material';
import SolarPowerIcon from '@mui/icons-material/SolarPower';

export default function SolarInsights({ selectedProspect, setStatus, yearlyConsumption }) {
  const [solarData, setSolarData] = useState(null);
  const [solarLoading, setSolarLoading] = useState(false);
  const [selectedPanels, setSelectedPanels] = useState(1);

  const getSolarInsights = async () => {
    if (!selectedProspect?.coords?.lat || !selectedProspect?.coords?.lng) {
      setStatus && setStatus({ notificationType: 'error', message: 'No coordinates available for this property' });
      return;
    }
    setSolarLoading(true);
    try {
      const { lat, lng } = selectedProspect.coords;
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        setStatus && setStatus({ notificationType: 'error', message: 'Google Maps API key not configured' });
        return;
      }
      const url = `https://solar.googleapis.com/v1/buildingInsights:findClosest?location.latitude=${lat}&location.longitude=${lng}&key=${apiKey}`;
      const response = await fetch(url);
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 404) {
          setSolarData({ noData: true });
          setStatus && setStatus({ notificationType: 'info', message: 'Solar data not available for this location' });
          return;
        }
        throw new Error(data.error?.message || 'Failed to fetch solar data');
      }
      setSolarData(data);
      console.log('Google Solar API response:', data);
      setStatus && setStatus({ notificationType: 'success', message: 'Solar insights retrieved successfully' });
    } catch (error) {
      console.error('Solar API error:', error);
      setStatus && setStatus({ notificationType: 'error', message: error.message || 'Failed to get solar insights' });
    } finally {
      setSolarLoading(false);
    }
  };

  // Calculate yearly energy for selected panels
  let yearlyEnergy = null;
  let maxPanels = solarData?.solarPotential?.maxArrayPanelsCount || 1;
  let maxYearlyKwh = solarData?.solarPotential?.maxArrayYearlyKwh || 0;
  if (solarData && maxPanels && maxYearlyKwh) {
    yearlyEnergy = Math.round((selectedPanels / maxPanels) * maxYearlyKwh * 10) / 10;
  }

  // Calculate offset percentage
  let offsetPercent = 0;
  if (yearlyEnergy && yearlyConsumption) {
    offsetPercent = Math.min(100, Math.round((yearlyEnergy / yearlyConsumption) * 1000) / 10);
  }

  // Suggest panels for 100% offset
  let suggestedPanels = null;
  if (maxPanels && maxYearlyKwh && yearlyConsumption) {
    suggestedPanels = Math.ceil((yearlyConsumption / maxYearlyKwh) * maxPanels);
    if (suggestedPanels > maxPanels) suggestedPanels = maxPanels;
  }

  // Reset slider when new data loads
  React.useEffect(() => {
    if (solarData?.solarPotential?.maxArrayPanelsCount) {
      setSelectedPanels(1);
    }
  }, [solarData?.solarPotential?.maxArrayPanelsCount]);

  return (
    <Card sx={{ mb: 3, bgcolor: '#222', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.15)', width: '100%', boxSizing: 'border-box' }}>
      <CardContent>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
          Solar Potential Insights
        </Typography>
        {solarData ? (
          solarData.noData ? (
            <Box>
              <Typography variant="body2" sx={{ color: '#f57c00', mb: 2, fontWeight: 500 }}>
                ⚠️ Solar data is currently not available for this location. Google's Solar API coverage is limited to certain areas.
              </Typography>
              <Typography variant="body2" sx={{ color: '#bbb', mb: 2 }}>
                This could be due to:
              </Typography>
              <Box component="ul" sx={{ color: '#bbb', pl: 2, mb: 2 }}>
                <li>Location not yet covered by Google's solar data</li>
                <li>Building too new or recently constructed</li>
                <li>Area with limited satellite imagery</li>
              </Box>
              <Button
                variant="outlined"
                onClick={getSolarInsights}
                disabled={solarLoading}
                sx={{ 
                  color: '#f57c00', 
                  borderColor: '#f57c00',
                  '&:hover': { borderColor: '#e65100', bgcolor: 'rgba(245, 124, 0, 0.04)' }
                }}
              >
                Try Again
              </Button>
            </Box>
          ) : (
            <Box>
              {/* Summary Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
                gap: 2,
                mb: 3,
                mt: 1
              }}>
                <Box sx={{ textAlign: 'center', bgcolor: '#181818', borderRadius: 2, p: 2 }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                    {solarData.solarPotential?.maxSunshineHoursPerYear ?? 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#bbb', fontWeight: 500 }}>
                    Sunshine (hrs/yr)
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', bgcolor: '#181818', borderRadius: 2, p: 2 }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                    {solarData.solarPotential?.maxArrayAreaMeters2 ? `${solarData.solarPotential.maxArrayAreaMeters2.toLocaleString()}` : 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#bbb', fontWeight: 500 }}>
                    Roof Area (m²)
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', bgcolor: '#181818', borderRadius: 2, p: 2 }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                    {solarData.solarPotential?.maxArrayPanelsCount ?? 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#bbb', fontWeight: 500 }}>
                    Max Panels
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', bgcolor: '#181818', borderRadius: 2, p: 2 }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                    {solarData.solarPotential?.carbonOffsetFactorKgPerMwh && solarData.solarPotential?.maxArrayYearlyKwh
                      ? Math.round(solarData.solarPotential.maxArrayYearlyKwh / 1000 * solarData.solarPotential.carbonOffsetFactorKgPerMwh).toLocaleString()
                      : 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#bbb', fontWeight: 500 }}>
                    CO₂ Savings (kg/yr)
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', bgcolor: '#181818', borderRadius: 2, p: 2 }}>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                    {solarData.solarPotential?.maxArrayYearlyKwh ? `${solarData.solarPotential.maxArrayYearlyKwh.toLocaleString()}` : 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#bbb', fontWeight: 500 }}>
                    Yearly Prod. (kWh)
                  </Typography>
                </Box>
              </Box>
              {/* Panel slider and yearly energy */}
              {solarData.solarPotential?.maxArrayPanelsCount && solarData.solarPotential?.maxArrayYearlyKwh && (
                <Box sx={{ mt: 1, mb: 2, p: 2, bgcolor: '#181818', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                    Select Number of Panels (400W each)
                  </Typography>
                  <Slider
                    value={selectedPanels}
                    min={1}
                    max={solarData.solarPotential.maxArrayPanelsCount}
                    step={1}
                    onChange={(_, val) => setSelectedPanels(val)}
                    sx={{ color: '#1976d2', mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#bbb' }}>
                      Panels: <strong>{selectedPanels}</strong> / {solarData.solarPotential.maxArrayPanelsCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#bbb' }}>
                      Yearly Energy: <strong>{yearlyEnergy ? yearlyEnergy.toLocaleString() : '0'} kWh</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#bbb', minWidth: 120 }}>
                      Offset: <strong>{offsetPercent}%</strong>
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={offsetPercent}
                      sx={{ height: 10, borderRadius: 5, flex: 1, bgcolor: '#333', '& .MuiLinearProgress-bar': { bgcolor: '#1976d2' } }}
                    />
                  </Box>
                  {suggestedPanels && (
                    <Typography variant="body2" sx={{ color: '#bbb', mt: 1 }}>
                      Panels needed for 100% offset: <strong>{suggestedPanels}</strong>
                    </Typography>
                  )}
                </Box>
              )}
              <Button
                variant="outlined"
                onClick={getSolarInsights}
                disabled={solarLoading}
                sx={{ 
                  color: '#1976d2', 
                  borderColor: '#1976d2',
                  '&:hover': { borderColor: '#1565c0', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                }}
              >
                Refresh Solar Data
              </Button>
            </Box>
          )
        ) : (
          <Box>
            <Typography variant="body2" sx={{ color: '#bbb', mb: 2 }}>
              Get detailed solar potential analysis including roof area, energy production estimates, and financial insights for this property.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SolarPowerIcon />}
              onClick={getSolarInsights}
              disabled={solarLoading}
              sx={{ 
                bgcolor: '#1976d2', 
                color: '#fff',
                '&:hover': { bgcolor: '#1565c0' },
                '&:disabled': { bgcolor: '#666' }
              }}
            >
              {solarLoading ? 'Loading Solar Data...' : 'Get Solar Insights'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
} 